import Dexie, { type Table } from "dexie";
import type { Catalog } from "./types";

export interface CachedCatalog {
  key: string; // `${userId}:${playlistId}`
  fetchedAt: number;
  data: Catalog;
}

export interface ResumeRow {
  key: string; // `${userId}:${playlistId}:${streamKind}:${streamId}`
  userId: number;
  playlistId: number;
  streamKind: "live" | "vod" | "series" | "catchup";
  streamId: string;
  title: string;
  poster?: string | null;
  position: number;
  duration: number;
  /** Series-level grouping id; populated for streamKind === 'series' so the
   * Hub continue-watching surface can deep-link via /series/{sid}/episodes/{eid}. */
  seriesId?: string | null;
  /** Catchup-only metadata. NULL on every other kind. Required for
   * stream_kind === 'catchup' continue-watching click-through. */
  catchupChannelId?: string | null;
  catchupStartMs?: number | null;
  catchupDurationMin?: number | null;
  catchupChannelName?: string | null;
  catchupProgrammeTitle?: string | null;
  updatedAt: number;
}

export interface FavoriteRow {
  key: string; // `${userId}:${playlistId}:${streamKind}:${streamId}`
  /** Server-side primary key. Present for rows synced from the server;
   * absent for the brief window between an optimistic insert and the
   * POST round-trip completing. */
  serverId?: number;
  userId: number;
  playlistId: number;
  // Catchup is folded into live (has_catchup flag), so the persisted
  // kinds are now exactly the server's three. The legacy "catchup" value
  // is preserved in the union only so old cache rows (pre-v3) don't
  // crash type-checks before reload wipes them.
  streamKind: "live" | "vod" | "series" | "catchup";
  streamId: string;
  title: string;
  poster?: string | null;
  /** Live channel that also has catchup — set when fav was created from
   * a live row whose tv_archive=1. */
  hasCatchup?: boolean;
  /** Catchup counterpart's stream id (for the smart-row "Watch live /
   * Catch up" picker). null when the channel doesn't have catchup. */
  catchupStreamId?: string | null;
  /** Top-down order; lower = higher in list. Mirrors server sort_index. */
  sortIndex?: number;
  addedAt: number;
}

export class BrainzrDB extends Dexie {
  catalogs!: Table<CachedCatalog, string>;
  resume!: Table<ResumeRow, string>;
  favorites!: Table<FavoriteRow, string>;

  constructor() {
    super("brainzr");
    // v1: original schema
    this.version(1).stores({
      catalogs: "key, fetchedAt",
      resume: "key, userId, playlistId, streamKind, updatedAt",
      favorites: "key, userId, playlistId, streamKind, addedAt",
    });
    // v2: catalog shape changed (added proxy_url fields). Drop cached catalogs
    // so the UI re-fetches with the new fields. Keep favorites/resume.
    this.version(2)
      .stores({
        catalogs: "key, fetchedAt",
        resume: "key, userId, playlistId, streamKind, updatedAt",
        favorites: "key, userId, playlistId, streamKind, addedAt",
      })
      .upgrade(async (tx) => {
        await tx.table("catalogs").clear();
      });
    // v3: favourites become a server-synced cache. Drop existing local
    // rows (server is now the source of truth; the next sync will refill
    // this table). New row fields: serverId, hasCatchup, catchupStreamId,
    // sortIndex.
    this.version(3)
      .stores({
        catalogs: "key, fetchedAt",
        resume: "key, userId, playlistId, streamKind, updatedAt",
        favorites: "key, serverId, userId, playlistId, streamKind, sortIndex, addedAt",
      })
      .upgrade(async (tx) => {
        await tx.table("favorites").clear();
      });
    // v4: resume positions become a server-synced cache. Same handoff
    // pattern as favourites — drop existing local rows so the next sync
    // pass refills from the server. Local-only resumes from before this
    // bump are lost on first load; the trade-off matches favs.
    this.version(4)
      .stores({
        catalogs: "key, fetchedAt",
        resume: "key, userId, playlistId, streamKind, updatedAt",
        favorites: "key, serverId, userId, playlistId, streamKind, sortIndex, addedAt",
      })
      .upgrade(async (tx) => {
        await tx.table("resume").clear();
      });
  }
}

export const db = new BrainzrDB();

export const catalogKey = (userId: number, playlistId: number) =>
  `${userId}:${playlistId}`;

export const itemKey = (
  userId: number,
  playlistId: number,
  kind: ResumeRow["streamKind"],
  id: string
) => `${userId}:${playlistId}:${kind}:${id}`;
